import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Search, 
  ChevronDown, 
  HelpCircle, 
  Share2, 
  Check, 
  ExternalLink, 
  Sparkles, 
  Zap, 
  BookOpen, 
  CheckCircle2, 
  ArrowRight,
  Filter
} from 'lucide-react';
import { FaqItem } from '../types';
import { SEO } from '../components/SEO';
import { useThemeTokens } from '../hooks/useActiveTheme';

const BUILTIN_FAQS: Record<string, FaqItem[]> = {
  cna: [
    {
      _id: 'cna-1',
      question: 'What testing vendors administer the CNA board exam?',
      answer: 'Most states contract with major testing organizations: Credentia (National Nurse Aide Assessment Program - NNAAP), Prometric, Pearson VUE, or Headmaster/D&S Diversified Technologies. Diamond School of Nursing study materials are aligned with testing standards across all major testing vendors.',
      certId: 'cna',
      category: 'Exam Format & Vendors',
      isPublished: true,
      order: 1
    },
    {
      _id: 'cna-2',
      question: 'What happens if I fail one component of the CNA exam?',
      answer: 'In virtually all states, the written knowledge test and clinical skills evaluation are scored independently. If you pass the written test but fail one skill, you only need to retest the clinical skills portion within your state\'s eligibility window (usually 2 years from course completion).',
      certId: 'cna',
      category: 'Passing Criteria',
      isPublished: true,
      order: 2
    },
    {
      _id: 'cna-3',
      question: 'What are the critical safety steps on the CNA Skills Test?',
      answer: 'Critical safety steps are non-negotiable procedural checkpoints that cause an automatic skill failure if missed. Key examples include washing hands before and after resident contact, locking bed and wheelchair brakes before initiating transfers, pulling privacy curtains before personal care, placing the call signal within easy reach, and never placing urinary catheter drainage bags above bladder level.',
      certId: 'cna',
      category: 'Clinical Skills',
      isPublished: true,
      order: 3
    },
    {
      _id: 'cna-4',
      question: 'How does Diamond School of Nursing help students pass on the first attempt?',
      answer: 'Our platform delivers realistic timed board simulations, full diagnostic rationale breakdowns, step-by-step clinical skill video guides and checklists, interactive dosage/fluid balance calculations, and spaced repetition flashcards calibrated to national exam standards.',
      certId: 'cna',
      category: 'Platform Benefits',
      isPublished: true,
      order: 4
    },
    {
      _id: 'cna-5',
      question: 'Can I try practice exams on this platform for free?',
      answer: 'Yes! Diamond School of Nursing provides free guest diagnostic access. You can explore study guides, take practice quizzes, review skills checklists, and flip smart flashcards immediately without creating an account or providing payment details.',
      certId: 'cna',
      category: 'Guest Access',
      isPublished: true,
      order: 5
    }
  ],
  phlebotomy: [
    {
      _id: 'phleb-1',
      question: 'What is the CLSI Order of Draw and why is it critical?',
      answer: 'The Order of Draw is a strict sequence established by the Clinical and Laboratory Standards Institute (CLSI) for collecting blood in additive tubes during venipuncture. It begins with sterile Blood Cultures, followed by Sodium Citrate (Light Blue), Serum (Red/Gold SST), Heparin (Green), EDTA (Lavender/Pink), and Sodium Fluoride/Potassium Oxalate (Gray). Following this sequence prevents additive cross-contamination that would invalidate test results.',
      certId: 'phlebotomy',
      category: 'Clinical Protocols',
      isPublished: true,
      order: 1
    },
    {
      _id: 'phleb-2',
      question: 'What certifications are available for Phlebotomy Technicians?',
      answer: 'Leading national credentialing agencies include the National Healthcareer Association (NHA - CPT), American Medical Technologists (AMT - RPT), American Society for Clinical Pathology (ASCP - PBT), and National Center for Competency Testing (NCCT - NCPT).',
      certId: 'phlebotomy',
      category: 'National Credentials',
      isPublished: true,
      order: 2
    },
    {
      _id: 'phleb-3',
      question: 'What vein is preferred for routine venipuncture?',
      answer: 'The median cubital vein in the antecubital fossa is the first choice because it is typically large, well-anchored, and least painful. The cephalic vein on the outer arm is second choice, and the basilic vein on the inner arm is the final choice due to proximity to the brachial artery and median nerve.',
      certId: 'phlebotomy',
      category: 'Anatomy & Technique',
      isPublished: true,
      order: 3
    },
    {
      _id: 'phleb-4',
      question: 'How long can a tourniquet remain on a patient\'s arm?',
      answer: 'A tourniquet must never remain on a patient\'s arm for longer than 60 seconds (one minute). Leaving the tourniquet on longer causes hemoconcentration and specimen hemolysis, which falsely elevates potassium, total protein, and red blood cell counts.',
      certId: 'phlebotomy',
      category: 'Specimen Safety',
      isPublished: true,
      order: 4
    }
  ],
  medical_assistant: [
    {
      _id: 'ma-1',
      question: 'What is the difference between a CMA and a CCMA?',
      answer: 'Both are accredited medical assisting credentials with equivalent clinical and administrative scope. "CMA" is awarded by the American Association of Medical Assistants (AAMA), while "CCMA" (Certified Clinical Medical Assistant) is credentialed by the National Healthcareer Association (NHA). Both are widely recognized by healthcare employers nationwide.',
      certId: 'medical_assistant',
      category: 'Credentials',
      isPublished: true,
      order: 1
    },
    {
      _id: 'ma-2',
      question: 'What injection angles and needle sizes does a Medical Assistant use?',
      answer: 'Intramuscular (IM) injections are administered at 90° with a 20G–23G, 1–1.5 inch needle; Subcutaneous (SubQ) injections are given at 45° (or 90° for short needles) with a 25G–27G, 3/8–5/8 inch needle; and Intradermal (ID) injections (such as TB tests) are administered at a 10°–15° angle with a 27G–28G, 1/2 inch needle.',
      certId: 'medical_assistant',
      category: 'Injections & Meds',
      isPublished: true,
      order: 2
    },
    {
      _id: 'ma-3',
      question: 'Can a Medical Assistant prescribe medications or give medical advice?',
      answer: 'No. Medical Assistants work under the delegated authority of a licensed physician, PA, or nurse practitioner. They cannot independently diagnose, prescribe medications, or provide independent clinical advice. They can transcribe provider orders and relay physician instructions.',
      certId: 'medical_assistant',
      category: 'Scope of Practice',
      isPublished: true,
      order: 3
    }
  ],
  pharmacy_tech: [
    {
      _id: 'pt-1',
      question: 'What is the passing score for the PTCB PTCE exam?',
      answer: 'The Pharmacy Technician Certification Exam (PTCE) is scored on a scaled range from 1000 to 1600. A passing score is 1400 or higher.',
      certId: 'pharmacy_tech',
      category: 'PTCE Board Exam',
      isPublished: true,
      order: 1
    },
    {
      _id: 'pt-2',
      question: 'Can a Pharmacy Technician give advice to patients about over-the-counter medications?',
      answer: 'No. Clinical counseling and drug consultations are strictly restricted to licensed pharmacists. If a patient asks about drug interactions, side effects, or OTC recommendations, the technician must immediately refer them to the pharmacist.',
      certId: 'pharmacy_tech',
      category: 'Scope of Practice',
      isPublished: true,
      order: 2
    },
    {
      _id: 'pt-3',
      question: 'What are DEA Controlled Substance Schedules?',
      answer: 'Controlled substances are classified by abuse potential: Schedule I (illicit drugs with no accepted medical use, e.g., heroin); Schedule II (high abuse potential, severe dependence, e.g., oxycodone, fentanyl; no refills allowed); Schedule III (moderate abuse, e.g., buprenorphine, ketamine); Schedule IV (low abuse, e.g., alprazolam, zolpidem); and Schedule V (lowest abuse, e.g., low-dose codeine cough syrups).',
      certId: 'pharmacy_tech',
      category: 'Federal Drug Law',
      isPublished: true,
      order: 3
    }
  ],
  pct: [
    {
      _id: 'pct-1',
      question: 'What is the difference between a CNA and a Patient Care Technician (PCT)?',
      answer: 'A Patient Care Technician (PCT or CPCT) is an advanced multi-skilled healthcare worker whose training builds upon core CNA competencies. While a CNA focuses primarily on Activities of Daily Living (ADLs) and basic nursing care in long-term care settings, a PCT acquires advanced acute clinical skills including venipuncture (phlebotomy), resting 12-lead EKG acquisition, sterile dressing changes, basic urinary catheter maintenance, and point-of-care capillary blood glucose testing.',
      certId: 'pct',
      category: 'Scope & Competencies',
      isPublished: true,
      order: 1
    },
    {
      _id: 'pct-2',
      question: 'What certifications are available for Patient Care Technicians?',
      answer: 'The most widely recognized national credential is the Certified Patient Care Technician/Assistant (CPCT/A) administered by the National Healthcareer Association (NHA). Other recognized credentials include the National Center for Competency Testing (NCCT - NCPCT) and the American Medical Certification Association (AMCA - PTC).',
      certId: 'pct',
      category: 'National Credentials',
      isPublished: true,
      order: 2
    },
    {
      _id: 'pct-3',
      question: 'What are the passing requirements for the NHA CPCT/A exam?',
      answer: 'The NHA CPCT/A exam consists of 100 scored multiple-choice questions plus 20 unscored pretest questions administered over 2 hours. A scaled score of 390 out of 500 is required to pass.',
      certId: 'pct',
      category: 'Exam Passing Score',
      isPublished: true,
      order: 3
    }
  ]
};

const CERT_NAMES: Record<string, { name: string; short: string }> = {
  cna: { name: 'Certified Nursing Assistant', short: 'CNA' },
  phlebotomy: { name: 'Phlebotomy Technician', short: 'Phlebotomy' },
  medical_assistant: { name: 'Certified Clinical Medical Assistant', short: 'Medical Assistant' },
  pharmacy_tech: { name: 'Pharmacy Technician', short: 'Pharmacy Tech' },
  pct: { name: 'Patient Care Technician', short: 'Patient Care Tech' }
};

function getAllBuiltinFaqs(): FaqItem[] {
  const all: FaqItem[] = [];
  Object.values(BUILTIN_FAQS).forEach(list => all.push(...list));
  return all;
}

/**
 * Synchronously retrieves initial FAQ data from DOM script tag or built-in defaults
 * Guarantees zero-flicker hydration on initial paint
 */
function getInitialFaqData(targetCert?: string): { faqs: FaqItem[]; certId: string } {
  const normId = targetCert ? targetCert.toLowerCase().trim() : 'all';

  if (typeof window !== 'undefined') {
    try {
      let data = (window as any).__INITIAL_FAQ_DATA__;
      if (!data) {
        const scriptEl = document.getElementById('__INITIAL_FAQ_DATA__');
        if (scriptEl && scriptEl.textContent) {
          data = JSON.parse(scriptEl.textContent);
        }
      }
      if (data && Array.isArray(data.faqs) && data.faqs.length > 0) {
        return { faqs: data.faqs, certId: data.certId || normId };
      }
    } catch (e) {
      console.warn('Initial FAQ script parsing notice:', e);
    }
  }

  // Fallback to built-in FAQs
  if (normId && normId !== 'all' && BUILTIN_FAQS[normId]) {
    return { faqs: BUILTIN_FAQS[normId], certId: normId };
  }
  return { faqs: getAllBuiltinFaqs(), certId: 'all' };
}

export const FaqPage: React.FC = () => {
  const tokens = useThemeTokens();
  const { certType } = useParams<{ certType?: string }>();
  const normalizedCert = certType ? certType.toLowerCase().trim() : 'all';

  // Synchronous initialization ensures ZERO hydration flicker
  const initialData = useMemo(() => getInitialFaqData(certType), [certType]);
  const [faqs, setFaqs] = useState<FaqItem[]>(initialData.faqs);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const firstId = initialData.faqs[0]?._id || 'cna-1';
    return new Set([firstId]);
  });
  const [copiedLink, setCopiedLink] = useState(false);

  const certInfo = normalizedCert !== 'all' && CERT_NAMES[normalizedCert] 
    ? CERT_NAMES[normalizedCert] 
    : { name: 'All Healthcare Certifications', short: 'All Specialties' };

  // Header font family class from tokens
  const headerFontFamily = useMemo(() => {
    return (tokens.headerFont || '')
      .split(' ')
      .filter(cls => !cls.startsWith('text-') && !cls.startsWith('dark:text-'))
      .join(' ');
  }, [tokens.headerFont]);

  // Non-blocking background sync with live FAQ API
  useEffect(() => {
    const fresh = getInitialFaqData(certType);
    if (fresh.faqs.length > 0) {
      setFaqs(fresh.faqs);
    }

    let isMounted = true;
    const url = normalizedCert !== 'all'
      ? `/practice-exams/api/faqs?certId=${normalizedCert}`
      : '/practice-exams/api/faqs';

    fetch(url)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!isMounted) return;
        if (data && Array.isArray(data.items) && data.items.length > 0) {
          setFaqs(data.items);
        }
      })
      .catch(err => console.warn('Silent background FAQ refresh notice:', err));

    return () => { isMounted = false; };
  }, [certType, normalizedCert]);

  // Dynamic SEO meta tags and Schema.org injection
  useEffect(() => {
    const pageTitle = `Frequently Asked Questions & Testing Guidelines (${certInfo.short}) | Diamond School of Nursing`;
    const pageDesc = `Authoritative answers to ${certInfo.short} certification questions: state testing vendors, passing score criteria, clinical skills requirements, and registry reciprocity.`;

    document.title = pageTitle;

    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'description');
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', pageDesc);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', pageDesc);

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    const canonicalUrl = normalizedCert !== 'all' 
      ? `https://study.diamondnursingschool.com/faq/${normalizedCert}`
      : 'https://study.diamondnursingschool.com/faq';
    canonicalTag.setAttribute('href', canonicalUrl);

    // Dynamic Schema.org FAQPage JSON-LD
    const existingSchemaScript = document.getElementById('faq-schema-jsonld');
    if (existingSchemaScript) {
      existingSchemaScript.remove();
    }

    if (faqs && faqs.length > 0) {
      const script = document.createElement('script');
      script.id = 'faq-schema-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": f.answer
          }
        }))
      });
      document.head.appendChild(script);
    }
  }, [normalizedCert, certInfo.short, faqs]);

  // Categories extracted from current dataset
  const categories = useMemo(() => {
    const set = new Set<string>();
    faqs.forEach(f => {
      if (f.category) set.add(f.category);
    });
    return ['All', ...Array.from(set)];
  }, [faqs]);

  // Filtered FAQs based on category and search query
  const filteredFaqs = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return faqs.filter(f => {
      const matchCat = selectedCategory === 'All' || f.category === selectedCategory;
      const matchQuery = !q || f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q);
      return matchCat && matchQuery;
    });
  }, [faqs, selectedCategory, searchQuery]);

  const toggleAccordion = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCopyLink = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  return (
    <div id="faq-page-container" className="w-full text-slate-900 dark:text-slate-100 font-sans">
      <SEO 
        title={`Frequently Asked Questions & Testing Guidelines (${certInfo.short})`}
        description={`Authoritative answers to ${certInfo.short} certification questions: state testing vendors, passing score criteria, clinical skills requirements, and registry reciprocity.`}
      />

      <article id="faq-directory-article" className="space-y-8 sm:space-y-10 pb-16 max-w-7xl mx-auto">
        
        {/* Top Breadcrumb & Share Bar matching ProgramDetailsPage */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-bold text-slate-500">
            <a href="/gateway" className="text-sky-600 hover:text-sky-700 transition-colors">Study Hub</a>
            <span className="text-slate-300">›</span>
            <Link to="/faq" className="text-slate-600 hover:text-slate-800 transition-colors">Frequently Asked Questions</Link>
            <span className="text-slate-300">›</span>
            <span className="text-slate-900 dark:text-white font-extrabold">{certInfo.short} Guidelines</span>
          </nav>

          <div className="flex items-center gap-2 text-xs">
            <span className="px-3 py-1 text-[10px] font-black uppercase tracking-widest bg-teal-50 text-[#0F7D6C] border border-teal-200 rounded-md">
              ● State Board Aligned
            </span>
            <button
              onClick={handleCopyLink}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
            >
              {copiedLink ? <Check size={13} className="text-emerald-500" /> : <Share2 size={13} />}
              <span>{copiedLink ? 'Copied' : 'Share'}</span>
            </button>
          </div>
        </div>

        {/* Hero Showcase matching ProgramDetailsPage layout & styling */}
        <header className="relative rounded-3xl overflow-hidden border border-slate-200/90 dark:border-slate-800 shadow-sm bg-slate-950 text-white">
          <div className="relative min-h-[340px] sm:min-h-[380px] flex flex-col justify-end p-6 sm:p-10 lg:p-12">
            <div className="absolute inset-0 z-0">
              <img 
                src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&w=1200&q=80" 
                alt="Healthcare Testing Guidelines" 
                className="w-full h-full object-cover object-center opacity-25"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/40"></div>
            </div>

            <div className="relative z-10 space-y-5 max-w-4xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-3 py-1 bg-white/15 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest text-sky-300 border border-white/10">
                  AUTHORITATIVE CANDIDATE KNOWLEDGEBASE
                </span>
                <span className="px-3 py-1 bg-emerald-500/20 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest text-emerald-300 border border-emerald-500/30">
                  100% Free Public Guide
                </span>
              </div>

              <h1 className={`text-2xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight ${headerFontFamily}`}>
                Frequently Asked Questions & Testing Guidelines ({certInfo.short})
              </h1>

              <p className="text-sm sm:text-base text-slate-200 font-medium leading-relaxed max-w-3xl">
                Authoritative guidance on state testing vendors, clinical training hours, examination registration, passing scores, and license reciprocity.
              </p>

              {/* Metrics Ribbon matching Guest Program page */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                  <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Verified Answers</p>
                  <p className="text-base sm:text-lg font-black text-white">{faqs.length} Solutions</p>
                </div>
                <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                  <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Testing Vendors</p>
                  <p className="text-base sm:text-lg font-black text-white">NNAAP • Prometric • NHA</p>
                </div>
                <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                  <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Passing Standard</p>
                  <p className="text-base sm:text-lg font-black text-emerald-300">70% – 80% Target</p>
                </div>
                <div className="p-3.5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15">
                  <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Reciprocity</p>
                  <p className="text-base sm:text-lg font-black text-sky-300">All 50 States</p>
                </div>
              </div>

              {/* Fast Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-3">
                <Link 
                  to={normalizedCert !== 'all' ? `/practice/${normalizedCert}/practice` : '/practice/cna/practice'}
                  className="px-6 py-4 text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg rounded-xl bg-[#0F7D6C] hover:bg-[#0B6356] text-white font-bold transition-all"
                >
                  <Zap size={14} />
                  <span>Start Free Practice</span>
                </Link>
                <a 
                  href="/gateway" 
                  className="px-6 py-4 text-xs uppercase tracking-wider flex items-center gap-2 rounded-xl bg-teal-50 hover:bg-teal-100 text-[#0F7D6C] border border-teal-200 font-bold transition-all"
                >
                  <BookOpen size={14} />
                  <span>Browse All Programs</span>
                </a>
                <Link 
                  to="/cna/tutor" 
                  className="px-6 py-4 text-xs uppercase tracking-wider flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold transition-all"
                >
                  <Sparkles size={14} />
                  <span>Ask AI Nurse Tutor</span>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Sticky Program Navigation Pills Bar */}
        <nav aria-label="Program Selection" className="sticky top-0 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md py-3 border-y border-slate-200 dark:border-slate-800 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <Link 
            to="/faq" 
            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 transition-all ${
              normalizedCert === 'all' 
                ? 'bg-[#0F7D6C] text-white shadow-sm' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            All Questions
          </Link>
          <Link 
            to="/faq/cna" 
            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 transition-all ${
              normalizedCert === 'cna' 
                ? 'bg-[#0F7D6C] text-white shadow-sm' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            CNA Nurse Aide
          </Link>
          <Link 
            to="/faq/phlebotomy" 
            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 transition-all ${
              normalizedCert === 'phlebotomy' 
                ? 'bg-[#0F7D6C] text-white shadow-sm' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            Phlebotomy Technician
          </Link>
          <Link 
            to="/faq/medical_assistant" 
            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 transition-all ${
              normalizedCert === 'medical_assistant' 
                ? 'bg-[#0F7D6C] text-white shadow-sm' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            Medical Assistant
          </Link>
          <Link 
            to="/faq/pharmacy_tech" 
            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 transition-all ${
              normalizedCert === 'pharmacy_tech' 
                ? 'bg-[#0F7D6C] text-white shadow-sm' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            Pharmacy Technician
          </Link>
          <Link 
            to="/faq/pct" 
            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shrink-0 transition-all ${
              normalizedCert === 'pct' 
                ? 'bg-[#0F7D6C] text-white shadow-sm' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            Patient Care Tech
          </Link>
        </nav>

        {/* Search and Category Filter Toolbar */}
        <div className="space-y-4">
          <div className="relative flex items-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs focus-within:ring-2 focus-within:ring-[#0F7D6C]/30 focus-within:border-[#0F7D6C] transition-all p-1.5">
            <Search className="text-slate-400 ml-3 shrink-0" size={18} />
            <input 
              type="text" 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={`Search ${certInfo.short} questions, skills checkpoints, testing vendors, or regulations...`}
              className="w-full bg-transparent px-3 py-2.5 outline-none text-slate-900 dark:text-white font-medium text-sm placeholder-slate-400"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="px-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 font-bold rounded-xl text-xs py-1.5 uppercase tracking-wider transition-colors mr-1"
              >
                Clear
              </button>
            )}
          </div>

          {/* Subcategory Pills */}
          {categories.length > 2 && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-xs font-bold text-slate-400 flex items-center gap-1 mr-1">
                <Filter size={12} /> Topic:
              </span>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                    selectedCategory === cat
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* FAQ Accordions Section */}
        <section className="space-y-4 pt-2">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, idx) => {
              const faqId = faq._id || `faq-item-${idx}`;
              const isOpen = expandedIds.has(faqId);
              return (
                <div 
                  key={faqId}
                  className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden bg-white dark:bg-slate-900 transition-all duration-200 shadow-xs hover:border-slate-300 dark:hover:border-slate-700"
                >
                  <button
                    type="button"
                    onClick={() => toggleAccordion(faqId)}
                    className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3.5">
                      <span className="w-8 h-8 rounded-xl bg-teal-50 dark:bg-teal-950/60 text-[#0F7D6C] dark:text-teal-400 border border-teal-200 dark:border-teal-800/60 flex items-center justify-center font-black text-xs shrink-0">
                        Q
                      </span>
                      <span className="font-extrabold text-sm sm:text-base text-slate-900 dark:text-slate-100">
                        {faq.question}
                      </span>
                    </div>
                    <ChevronDown 
                      size={18} 
                      className={`text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#0F7D6C]' : ''}`} 
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-6 sm:px-6 sm:pb-6 pt-2 border-t border-slate-100 dark:border-slate-800 text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-normal animate-fade-in">
                      <p className="mb-3">{faq.answer}</p>
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 dark:bg-teal-950/40 text-[#0F7D6C] dark:text-teal-300 text-[10px] font-bold rounded-md border border-teal-100 dark:border-teal-900/60">
                          <CheckCircle2 size={12} className="text-[#0F7D6C]" />
                          Verified by Diamond Nursing Faculty
                        </span>
                        {faq.category && (
                          <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold rounded-md">
                            {faq.category}
                          </span>
                        )}
                        {faq.certId && (
                          <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold rounded-md uppercase">
                            {faq.certId === 'all' ? 'Universal' : faq.certId}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
              <HelpCircle className="mx-auto text-slate-400" size={32} />
              <p className="font-bold text-slate-800 dark:text-slate-200">No questions found matching your search</p>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Try searching for different terms like "clinical skills", "testing vendors", "reciprocity", or clear your filter.
              </p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-800 rounded-xl text-xs font-bold hover:bg-slate-300 transition-colors"
              >
                Reset Search Filters
              </button>
            </div>
          )}
        </section>

        {/* Footer Conversion Callout matching ProgramDetailsPage */}
        <footer className={`p-8 sm:p-12 text-center rounded-3xl shadow-md space-y-6 ${tokens.heroBannerBg} text-white relative overflow-hidden`}>
          <div className="relative z-10 max-w-2xl mx-auto space-y-4">
            <h2 className="text-xl sm:text-3xl font-black">
              Have an Unanswered Testing Question?
            </h2>
            <p className="text-sm text-white/80 leading-relaxed">
              Ask our AI Nurse Tutor or contact the Diamond School of Nursing faculty advising desk for personalized guidance.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <Link
                to="/cna/tutor"
                className="px-6 py-3 rounded-2xl bg-white text-slate-900 font-black text-xs uppercase tracking-wider shadow-lg hover:bg-slate-100 transition-all inline-flex items-center gap-2"
              >
                Ask AI Clinical Tutor
              </Link>
              <a
                href="https://www.diamondnursingschool.com/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-black text-xs uppercase tracking-wider border border-white/20 transition-all inline-flex items-center gap-2"
              >
                Faculty Advising Desk
              </a>
            </div>
          </div>
        </footer>

      </article>
    </div>
  );
};
